import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// ============================================
// Async Thunks
// ============================================

// Get paginated list of templates (POST /list endpoint)
export const getImportTemplates = createAsyncThunk(
  "ImportTemplate/getList",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const { data } = await axios.post(`${API_URL.IMPORT_TEMPLATE.LIST}`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get templates by entity type
export const getImportTemplatesByEntity = createAsyncThunk(
  "ImportTemplate/getByEntity",
  async (entityType, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.IMPORT_TEMPLATE.BY_ENTITY(entityType));
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get single template by ID
export const getImportTemplate = createAsyncThunk(
  "ImportTemplate/getById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.IMPORT_TEMPLATE.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Create new template
export const createImportTemplate = createAsyncThunk(
  "ImportTemplate/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL.IMPORT_TEMPLATE.SLUG}`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Update template
export const updateImportTemplate = createAsyncThunk(
  "ImportTemplate/update",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.IMPORT_TEMPLATE.SLUG}`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Delete template
export const deleteImportTemplate = createAsyncThunk(
  "ImportTemplate/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.IMPORT_TEMPLATE.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Clone template
export const cloneImportTemplate = createAsyncThunk(
  "ImportTemplate/clone",
  async ({ id, newTemplateName }, { rejectWithValue }) => {
    try {
      // newTemplateName is sent as query parameter (backend uses [FromQuery])
      const { data } = await axios.post(
        `${API_URL.IMPORT_TEMPLATE.CLONE(id)}?newTemplateName=${encodeURIComponent(newTemplateName)}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Set template as default
export const setDefaultImportTemplate = createAsyncThunk(
  "ImportTemplate/setDefault",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.IMPORT_TEMPLATE.SET_DEFAULT(id));
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Get importable fields for entity type
export const getImportableFields = createAsyncThunk(
  "ImportTemplate/getImportableFields",
  async (entityType, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.IMPORT_TEMPLATE.IMPORTABLE_FIELDS(entityType));
      return { entityType, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get importable fields grouped by level (Header/Line)
export const getImportableFieldsGrouped = createAsyncThunk(
  "ImportTemplate/getImportableFieldsGrouped",
  async (entityType, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.IMPORT_TEMPLATE.IMPORTABLE_FIELDS_GROUPED(entityType));
      return { entityType, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get supported file formats
export const getSupportedFormats = createAsyncThunk(
  "ImportTemplate/getSupportedFormats",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.IMPORT_TEMPLATE.SUPPORTED_FORMATS);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// ============================================
// Slice Definition
// ============================================

const importTemplateSlice = createSlice({
  name: "importTemplate",
  initialState: {
    // List data
    template_list: [],
    pagination: null,
    active_payload: null,
    total_records: 0,

    // Current template being viewed/edited
    current_template: null,

    // Importable fields metadata (cached by entity type)
    importable_fields: {}, // { PurchaseOrder: [...], Item: [...] }
    importable_fields_grouped: {}, // { PurchaseOrder: { Header: [...], Line: [...] } }

    // Supported formats
    supported_formats: [],

    // Loading states
    loading: {
      list: false,
      detail: false,
      save: false,
      clone: false,
      fields: false,
    },
  },
  reducers: {
    setCurrentTemplate(state, action) {
      state.current_template = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentTemplate(state) {
      state.current_template = null;
    },
    resetTemplateList(state) {
      state.template_list = [];
      state.pagination = null;
      state.current_template = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get templates list
      .addCase(getImportTemplates.pending, (state) => {
        state.loading.list = true;
      })
      .addCase(getImportTemplates.fulfilled, (state, action) => {
        state.loading.list = false;
        if (action.payload?.data?.list) {
          state.template_list = action.payload.data.list;
          state.total_records = action.payload.data.totalRecords || action.payload.data.total || 0;
          state.pagination = {
            pageNumber: action.payload.data.pageNumber,
            pageSize: action.payload.data.pageSize,
            total: action.payload.data.total,
          };
        } else if (Array.isArray(action.payload?.data)) {
          state.template_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.template_list = action.payload;
        } else {
          state.template_list = [];
        }
      })
      .addCase(getImportTemplates.rejected, (state) => {
        state.loading.list = false;
        state.template_list = [];
      })

      // Get templates by entity type
      .addCase(getImportTemplatesByEntity.fulfilled, (state, action) => {
        if (Array.isArray(action.payload?.data)) {
          state.template_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.template_list = action.payload;
        } else {
          state.template_list = [];
        }
      })

      // Get single template
      .addCase(getImportTemplate.pending, (state) => {
        state.loading.detail = true;
      })
      .addCase(getImportTemplate.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.current_template = action.payload?.data || action.payload;
      })
      .addCase(getImportTemplate.rejected, (state) => {
        state.loading.detail = false;
        state.current_template = null;
      })

      // Create template
      .addCase(createImportTemplate.pending, (state) => {
        state.loading.save = true;
      })
      .addCase(createImportTemplate.fulfilled, (state) => {
        state.loading.save = false;
      })
      .addCase(createImportTemplate.rejected, (state) => {
        state.loading.save = false;
      })

      // Update template
      .addCase(updateImportTemplate.pending, (state) => {
        state.loading.save = true;
      })
      .addCase(updateImportTemplate.fulfilled, (state) => {
        state.loading.save = false;
      })
      .addCase(updateImportTemplate.rejected, (state) => {
        state.loading.save = false;
      })

      // Clone template
      .addCase(cloneImportTemplate.pending, (state) => {
        state.loading.clone = true;
      })
      .addCase(cloneImportTemplate.fulfilled, (state) => {
        state.loading.clone = false;
      })
      .addCase(cloneImportTemplate.rejected, (state) => {
        state.loading.clone = false;
      })

      // Get importable fields
      .addCase(getImportableFields.pending, (state) => {
        state.loading.fields = true;
      })
      .addCase(getImportableFields.fulfilled, (state, action) => {
        state.loading.fields = false;
        const { entityType, data } = action.payload;
        state.importable_fields[entityType] = data?.data || data || [];
      })
      .addCase(getImportableFields.rejected, (state) => {
        state.loading.fields = false;
      })

      // Get importable fields grouped
      .addCase(getImportableFieldsGrouped.pending, (state) => {
        state.loading.fields = true;
      })
      .addCase(getImportableFieldsGrouped.fulfilled, (state, action) => {
        state.loading.fields = false;
        const { entityType, data } = action.payload;
        state.importable_fields_grouped[entityType] = data?.data || data || {};
      })
      .addCase(getImportableFieldsGrouped.rejected, (state) => {
        state.loading.fields = false;
      })

      // Get supported formats
      .addCase(getSupportedFormats.fulfilled, (state, action) => {
        state.supported_formats = action.payload?.data || action.payload || [];
      });
  },
});

export const {
  setCurrentTemplate,
  setActivePayload,
  resetCurrentTemplate,
  resetTemplateList,
} = importTemplateSlice.actions;

export default importTemplateSlice.reducer;
